/**
 * The regions a person can be from, and what they are called.
 *
 * **From CLDR through `Intl.DisplayNames`, the same source `bootstrap-platform.ts` fills
 * `platform.country` from**, so the picker, the validator and the reference table cannot
 * disagree about which codes exist. Nothing is shipped: every browser and every Node on Lambda
 * carries the data already, and names come out in the reader's language.
 *
 * CLDR is a superset of ISO 3166-1. It carries a handful of exceptionally reserved codes (`AC`,
 * `TA`, `IC`) that are real places, and a few entries that are not places at all — those are the
 * ones excluded here, `XA` and `XB` being pseudo-locales.
 */

const NOT_A_COUNTRY = new Set(['EU', 'EZ', 'UN', 'QO', 'XA', 'XB'])

export type Country = { code: string; name: string }

const cache = new Map<string, readonly Country[]>()

/**
 * Every region, sorted by its name in `locale`.
 *
 * Brute-forcing the 676 two-letter combinations is a filter rather than a fabrication:
 * `fallback: 'none'` returns undefined for a code CLDR does not know. Computed once per locale.
 */
export function countries(locale = 'en'): readonly Country[] {
  const hit = cache.get(locale)
  if (hit !== undefined) return hit

  const display = new Intl.DisplayNames([locale, 'en'], { type: 'region', fallback: 'none' })
  const rows: Country[] = []
  for (let first = 65; first <= 90; first += 1) {
    for (let second = 65; second <= 90; second += 1) {
      const code = String.fromCodePoint(first, second)
      if (NOT_A_COUNTRY.has(code)) continue
      const name = display.of(code)
      if (name !== undefined && name !== code) rows.push({ code, name })
    }
  }
  rows.sort((a, b) => a.name.localeCompare(b.name, locale))
  cache.set(locale, rows)
  return rows
}

let known: ReadonlySet<string> | undefined

/** An ISO 3166-1 alpha-2 code CLDR recognizes. Upper case only: that is what is stored. */
export function isCountryCode(value: unknown): value is string {
  if (typeof value !== 'string' || !/^[A-Z]{2}$/.test(value)) return false
  known ??= new Set(countries().map((country) => country.code))
  return known.has(value)
}

/** The region's name, or the code itself when there is none to show. */
export function countryName(code: string, locale = 'en'): string {
  return countries(locale).find((country) => country.code === code)?.name ?? code
}

/**
 * A code from what somebody typed: `ID`, `id`, `Indonesia`, `indonesia`.
 *
 * For spreadsheet imports, where a column of country names is far more common than a column of
 * codes. Names match in English, whole and case-insensitive; a near miss is no match.
 */
export function countryFromText(text: string): string | undefined {
  const trimmed = text.trim()
  const upper = trimmed.toUpperCase()
  if (isCountryCode(upper)) return upper
  const lower = trimmed.toLowerCase()
  return countries().find((country) => country.name.toLowerCase() === lower)?.code
}
